import { Tab, Tabs } from '@material-ui/core'
import { Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ActivityIndicator, StyleProp, View, ViewStyle, Text } from 'react-native'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { PolicyDispatchingService } from './services/PolicyDispatchingService'
import { borderedInputStyle } from './styles/policyDispatchingStyles'
import { getPermissions } from './utils/policyDispatchingUtils'

const tabs = [
  { id: 1, name: 'Thông tin phân phối' },
  { id: 2, name: 'Thông tin kho' },
  { id: 3, name: 'Thư xác nhận' }
]

type ControlledInputProps = {
  control: any
  name: string
  title: string
  required?: boolean
  containerStyle?: StyleProp<ViewStyle>
  disabled?: boolean
}

type ContractInformationForm = Record<string, string | boolean> & {
  contractNumber: string
}

const emptyFormValues: ContractInformationForm = {
  contractNumber: ''
}

const ControlledInput = ({ control, name, title, required, containerStyle, disabled = true }: ControlledInputProps) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <Input
          title={title}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          errorMessage={error?.message}
          containerStyle={containerStyle as any}
          required={required}
          inputStyle={borderedInputStyle}
          filled
          alwayShowUnderline
          disabled={disabled}
        />
      )}
    />
  )
}

const ContractSearchSection = ({
  control,
  allowSearch,
  onSearch,
  isLoading
}: {
  control: any
  allowSearch: boolean
  onSearch: () => void
  isLoading: boolean
}) => {
  return (
    <View
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 24, ...styles.column_1_4 } as any}
    >
      <ControlledInput
        control={control}
        name="contractNumber"
        title="Số hợp đồng"
        required
        containerStyle={{ flex: 1 }}
        disabled={false}
      />
      <button
        style={isLoading || !allowSearch ? styles.disabledButton : styles.submitButton}
        onClick={onSearch}
        disabled={isLoading || !allowSearch}
      >
        <span style={styles.mr_5}>
          <assets.SearchWhite />
        </span>
        Tìm
      </button>
    </View>
  )
}

const ContractDetailsSection = ({ control }: { control: any }) => {
  return (
    <View style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="crossReferenceNumber" title="Số HSYCBH" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="statusDescription" title="Tình trạng" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="branch" title="Chi nhánh phục vụ" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="issueDates" title="Các ngày phát hành" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="firstIssueDate" title="Ngày phát hành đầu tiên" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="contractOwnerName" title="Chủ hợp đồng" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="lasRecordedDate" title="Ngày LAS ghi nhận" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="officeCodes" title="Mã văn phòng" />
        </View>
      </View>
    </View>
  )
}

const AgentInformationSection = ({ control }: { control: any }) => {
  return (
    <View style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={{ fontSize: '18px', fontWeight: '600' } as any}>Thông tin đại lý</View>
      <View style={styles.row}>
        <View style={{ width: '20%' } as any}>
          <ControlledInput control={control} name="agentNumber" title="Đại lý phục vụ" />
        </View>
        <View style={{ width: '80%', marginTop: 22 } as any}>
          <ControlledInput control={control} name="agentName" title="" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentStatus" title="Tình trạng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentType" title="Loại" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="dateAppointed" title="Ngày gia nhập" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="dateTerminate" title="Ngày kết thúc" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="gaBp" title="GA/BP" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentSalesUnitCode" title="Nhóm" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="area" title="Khu vực" />
        </View>
        <View style={styles.column_1_2} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentSalesBranch" title="Văn phòng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentBranch" title="Ban" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="saleZone" title="Vùng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="supportingAgentName" title="Đại lý hỗ trợ" />
        </View>
      </View>
    </View>
  )
}

const DistributionInformation = ({ control }: { control: any }) => {
  return (
    <View style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="deliveryDate" title="Ngày giao hợp đồng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="deliveryOffice" title="Văn phòng giao hợp đồng" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="receiver" title="Người nhận" />
        </View>
      </View>
    </View>
  )
}

const WarehouseInformation = ({ control }: { control: any }) => {
  return (
    <View style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.row}>
          <View style={styles.column_1_2}>
            <ControlledInput control={control} name="stockInDate" title="Ngày nhập kho" />
          </View>
          <View style={styles.column_1_2}>
            <ControlledInput control={control} name="stockInOffice" title="Văn phòng nhập kho" />
          </View>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="stockInUser" title="Nhân viên nhập kho" />
        </View>
        <View style={styles.column_1_2} />
      </View>

      <View style={styles.row}>
        <View style={styles.row}>
          <View style={styles.column_1_2}>
            <ControlledInput control={control} name="returnDate" title="Ngày trả lại" />
          </View>
          <View style={styles.column_1_2}>
            <ControlledInput control={control} name="returnOffice" title="Văn phòng trả lại" />
          </View>
        </View>
      </View>
    </View>
  )
}

const ConfirmationLetter = ({ control }: { control: any }) => {
  return (
    <View style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="letterEntryDate" title="Ngày BO nhập thư" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="letterOffice" title="Văn phòng nhận thư" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="letterType" title="Loại thư" />
        </View>
        <View style={styles.column_1_2} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="letterEntryUser" title="Nhân viên nhập thư" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="remark" title="Ghi chú" />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="overdueDays" title="Số ngày trả" />
        </View>
        <View style={styles.column_1_2} />
      </View>
    </View>
  )
}

export default function PDOldContractInformationScreen() {
  const [value, setSelectedTab] = React.useState(0)
  const [isLoading, setIsLoading] = React.useState(false)
  const [openModal, setOpenModal] = React.useState(false)
  const [modalTitle, setModalTitle] = React.useState('Thông báo')
  const [modalMessage, setModalMessage] = React.useState('')
  const { control, getValues, reset } = useForm<ContractInformationForm>({ defaultValues: emptyFormValues })
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.QuickSearchSearch)

  const clearDetails = () => reset({ ...emptyFormValues, contractNumber: getValues('contractNumber') })

  const handleSearch = () => {
    const contractNumber = getValues('contractNumber')?.trim()
    if (!contractNumber) {
      setModalTitle('Thông báo')
      setModalMessage('Vui lòng nhập số hợp đồng.')
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchOldQuickPolicyInformation(contractNumber),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (response && 'body' in response && response.body) {
            const data = response.body
            const contract = data.contractInformation
            const agent = data.agentInformation
            const distribution = data.distributionInformation
            const stock = data.stockInformation
            const letter = data.ackLetters[0]

            reset({
              ...emptyFormValues,
              contractNumber: contract?.contractNumber || contractNumber,
              crossReferenceNumber: contract?.proposalNumber || '',
              statusDescription: contract?.statusDescription || contract?.statusCode || '',
              branch: contract?.servicingBranch || '',
              issueDates: contract?.issueDates?.join(', ') || '',
              firstIssueDate: contract?.firstIssueDate || '',
              contractOwnerName: contract?.contractOwnerName || '',
              lasRecordedDate: contract?.lasRecordedDate || '',
              officeCodes: contract?.officeCode || '',
              agentNumber: agent?.agentNumber || '',
              agentName: agent?.agentName || '',
              agentStatus: agent?.status || '',
              agentType: agent?.type || '',
              dateAppointed: agent?.dateAppointed || '',
              dateTerminate: agent?.dateTerminate || '',
              gaBp: agent?.gaBp || '',
              agentSalesUnitCode: agent?.unitCode || '',
              area: agent?.regionCode || '',
              agentSalesBranch: agent?.officeCode || '',
              agentBranch: agent?.branchCode || '',
              saleZone: agent?.zoneCode || '',
              supportingAgentName: agent?.supportingAgentName || '',
              deliveryDate: distribution?.deliveryDate || '',
              deliveryOffice: distribution?.deliveryOffice || '',
              receiver: distribution?.receiver || '',
              stockInDate: stock?.stockInDate || '',
              stockInOffice: stock?.stockInOffice || '',
              stockInUser: stock?.stockInEmployee || '',
              returnDate: stock?.returnDate || '',
              returnOffice: stock?.returnOffice || '',
              customerSignedDate: letter?.customerSignedDate || '',
              letterEntryDate: letter?.letterEntryDate || '',
              letterOffice: letter?.letterOffice || '',
              letterType: letter?.letterType || '',
              letterEntryUser: letter?.letterEmployee || '',
              remark: letter?.remark || '',
              overdueDays: letter?.overdueDays?.toString() || ''
            })
          } else {
            clearDetails()
            setModalTitle('Thông báo')
            setModalMessage('Không tìm thấy thông tin hợp đồng.')
            setOpenModal(true)
          }
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          clearDetails()
          setModalTitle('Lỗi')
          setModalMessage('Có lỗi xảy ra khi tìm kiếm.')
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => setSelectedTab(newValue)

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Tìm nhanh</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>Thông tin hợp đồng</Text>
        <Text style={styles.noted}>(* thông tin cần nhập)</Text>
        <ContractSearchSection
          control={control}
          allowSearch={allowSearch}
          onSearch={handleSearch}
          isLoading={isLoading}
        />
        <ContractDetailsSection control={control} />
        <AgentInformationSection control={control} />
        <View style={{ marginTop: 28 } as any}>
          <Text style={{ fontSize: '18px', fontWeight: '600' } as any}>
            {value === 0 ? 'Thông tin phân phối' : value === 1 ? 'Thông tin kho' : 'Thư xác nhận'}
          </Text>
          <View style={{ marginTop: 16 } as any}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="stock out tabs"
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.id}
                  style={{
                    ...(value === index ? styles.selectedTab : styles.unselectedTab),
                    marginRight: index === tabs.length - 1 ? 0 : 8
                  }}
                  label={tab.name}
                  id={`distribution-tab-${index}`}
                  aria-controls={`distribution-tabpanel-${index}`}
                />
              ))}
            </Tabs>
            {value === 0 && <DistributionInformation control={control} />}
            {value === 1 && <WarehouseInformation control={control} />}
            {value === 2 && <ConfirmationLetter control={control} />}
          </View>
        </View>
      </View>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalTitle}
        message={modalMessage}
        onClose={() => setOpenModal(false)}
      />
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    marginBottom: 12,
    width: 'fit-content'
  },
  noted: {
    fontSize: '12px',
    color: '#6a7282',
    fontStyle: 'italic',
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    width: '100%'
  },
  column_1_4: {
    width: 'calc(100% / 4 - 18px)'
  },
  column_1_2: {
    width: 'calc(100% / 2 - 12px)'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  selectedTab: {
    backgroundColor: '#ED1B2E',
    paddingTop: 4,
    paddingRight: 20,
    paddingBottom: 4,
    paddingLeft: 20,
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '10px',
    minWidth: 'auto',
    minHeight: 'auto',
    textTransform: 'none' as const
  },
  unselectedTab: {
    paddingTop: 4,
    paddingRight: 20,
    paddingBottom: 4,
    paddingLeft: 20,
    color: '#000',
    minWidth: 'auto',
    minHeight: 'auto',
    textTransform: 'none' as const,
    borderRadius: '10px',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#6a7282'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.5)'
  }
}
